import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { fmtDay } from "@/lib/plans";
import { StartPlan, ManagePlan } from "./BillingForms";

const today = () => new Date().toISOString().slice(0, 10);

const statusText = (b) => {
  if (b.status === "free") return "Your company uses Cross Jobs for free. There's nothing to pay.";
  if (b.status === "trial") return `Free trial: ${b.days_left} day${b.days_left === 1 ? "" : "s"} left (ends ${fmtDay(b.trial_ends_at)}). No limits during the trial.`;
  if (b.status === "past_due") return "Your last card payment didn't go through. Update your card to keep the office dashboard open.";
  if (b.status === "locked") return "Your plan isn't active, so the office dashboard is locked. Your crew can still clock on.";
  const first = b.start_date && b.start_date > today() ? ` First charge on ${fmtDay(b.start_date)}.` : "";
  const next = b.next ? ` Switching to ${b.next.plan_name} (${b.next.cadence}) from ${fmtDay(b.next.date)}.` : "";
  return `${b.plan_name} plan, billed ${b.cadence}.${first}${next}${b.cancel_date ? ` Ends on ${fmtDay(b.cancel_date)}.` : ""}`;
};

// Plan & billing: status, plan picker, Square card form and plan actions (company login only).
export const BillingPanel = ({ onDone }) => {
  const { user } = useAuth();
  const { refresh } = useCompany();
  const [b, setB] = useState(null);
  const [sel, setSel] = useState({ plan: "team", cadence: "monthly" });
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.billing().then((x) => {
      setB(x);
      const fit = x.plans.filter((p) => p.fits).map((p) => p.id);
      const cur = x.next || x;
      setSel({ plan: cur.plan || (fit.includes("team") ? "team" : fit[0]), cadence: cur.cadence || "monthly" });
    }).catch(() => toast.error("Could not load your plan"));
  }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try { setB(await fn()); await refresh(); toast.success(msg); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (!b) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const [givenName, ...rest] = (user?.name || "").split(" ");
  const props = { b, sel, setSel, busy, run, cardRef, contact: { email: user?.email, givenName, familyName: rest.join(" ") } };
  const tone = b.status === "past_due" || b.status === "locked" ? "border-destructive/40 bg-destructive/5" : b.ending_soon ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-secondary/40";
  return (
    <div className="space-y-5" data-testid="billing-panel">
      <div className={`rounded-lg border p-4 ${tone}`}>
        <p className="text-sm font-medium" data-testid="billing-status">{statusText(b)}</p>
        <p className="text-xs text-muted-foreground mt-1" data-testid="billing-usage">You have {b.usage.depots} depot{b.usage.depots === 1 ? "" : "s"} and {b.usage.crew} crew.</p>
      </div>
      {b.status === "free" ? null
        : !b.can_manage ? <p className="text-sm text-muted-foreground" data-testid="billing-ask-admin">Only your company login can pick or change the plan.</p>
          : b.plan ? <ManagePlan {...props} /> : <StartPlan {...props} />}
    </div>
  );
};
